// Browser audio helpers for voice-clone recording.
//
// MediaRecorder produces webm/opus (Chrome) or mp4 (Safari), which the clone
// API doesn't accept directly — so recordings are decoded with WebAudio and
// re-encoded as 16-bit mono WAV, which is universally accepted.

export async function blobToWavFile(blob: Blob, filename = 'recording.wav'): Promise<File> {
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const wav = encodeWav(decoded);
    return new File([wav], filename, { type: 'audio/wav' });
  } finally {
    void ctx.close();
  }
}

function encodeWav(buffer: AudioBuffer): ArrayBuffer {
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;

  // Mix down to mono
  const mono = new Float32Array(numFrames);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < numFrames; i++) mono[i] += data[i] / buffer.numberOfChannels;
  }

  const bytesPerSample = 2;
  const dataSize = numFrames * bytesPerSample;
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);

  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numFrames; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, mono[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return out;
}
